import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  Output,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { connectState, InputObservable } from '@y42/angular/util';
import { ContentChange, QuillEditorComponent, QuillModules } from 'ngx-quill';
import 'quill-mention';
import { BehaviorSubject, debounceTime, distinctUntilChanged, filter, Observable, tap } from 'rxjs';
import { ChatAvatar, ChatRoomMessageUIPayload, MentionUser } from '../../models/chat-room.model';
import { ChatAvatarComponent } from '../chat-avatar/chat-avatar.component';

interface UserMentionItem {
  id: number;
  value: string;
  email: string;
  profilePic?: string;
}

interface Quill {
  focus: () => void;
  setSelection: (range: number) => void;
}

@UntilDestroy()
@Component({
  selector: 'y42-chat-input',
  templateUrl: './chat-input.component.html',
  styleUrls: ['./chat-input.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ChatInputComponent implements AfterViewInit {
  constructor(private readonly element: ElementRef, private readonly viewContainerRef: ViewContainerRef) {}

  @Input()
  @InputObservable()
  mode: 'create' | 'edit' = 'create';
  mode$!: Observable<'create' | 'edit'>;

  @Input() avatar: ChatAvatar = { full_name: '', imgSrc: null, isUnknown: true };
  @Input() mentionUsers: MentionUser[] = [];

  @Input() set initialComment(comment: string) {
    this.commentForm.controls.comment.setValue(comment);
  }

  @Output() commented = new EventEmitter<ChatRoomMessageUIPayload>();
  @Output() canceled = new EventEmitter<void>();

  @ViewChild('quilleditor') quillEditor?: QuillEditorComponent;

  private editorInstance?: Quill;
  private matchedUsers: MentionUser[] = [];

  private readonly lastTextValue$ = new BehaviorSubject<string>('');
  private readonly lastHtmlValue$ = new BehaviorSubject<string>('');

  public readonly commentForm = new FormGroup<{ comment: FormControl<string> }>({
    comment: new FormControl<string>('', { nonNullable: true }), // TODO: max length?
  });

  public readonly formats: string[] = ['mention'];

  public readonly quillConfig: QuillModules = {
    mention: {
      allowedChars: /^[A-Za-z\sÅÄÖåäö]*$/,
      positioningStrategy: 'absolute',
      onSelect: (item: { value: string; index: number }, insertItem: (val: { value: string }) => void) => {
        const matchedUser = this.matchedUsers[item.index];
        item.value = matchedUser?.full_name ?? '';
        insertItem(item);
      },
      fixMentionsToQuill: true,
      mentionDenotationChars: ['@'],
      source: async (searchTerm: string, renderList: (suggestions: Array<{ id: number; value: string }>) => void) => {
        let matches = [...this.mentionUsers];
        if (searchTerm.length !== 0) {
          const lowerSearchTerm = searchTerm.toLowerCase();
          matches = this.mentionUsers.filter((user) => {
            return user.full_name.toLowerCase().includes(lowerSearchTerm);
          });
        }

        this.matchedUsers = matches;
        const idAndValues: UserMentionItem[] = matches.map((match) => ({
          id: match.id,
          value: `${match.full_name}`,
          email: match.email,
          profilePic: match.profile_pic,
        }));
        renderList(idAndValues);
      },
      renderItem: (data: UserMentionItem) => {
        return this.mentionItemTemplate(data);
      },
    },
    keyboard: {
      bindings: {
        enter: {
          key: 13,
          handler: () => {
            this.submit();
          },
        },
      },
    },
  };

  public readonly state = connectState(this, {
    lastTextValue: this.lastTextValue$,
  });

  @HostListener('keydown.arrowup')
  @HostListener('keydown.arrowdown')
  onArrowUpOrDown() {
    const selectedMentionItem = this.element.nativeElement.querySelector('.ql-mention-list-item.selected');
    if (selectedMentionItem) {
      if (selectedMentionItem.scrollIntoViewIfNeeded) {
        selectedMentionItem.scrollIntoViewIfNeeded();
      } else if (selectedMentionItem.scrollIntoView) {
        selectedMentionItem.scrollIntoView();
      }
    }
  }

  ngAfterViewInit() {
    this.mode$
      .pipe(
        distinctUntilChanged(),
        filter((mode) => mode === 'edit'),
        debounceTime(500),
        tap(() => {
          this.editorInstance?.focus();
          this.editorInstance?.setSelection(Number.MAX_SAFE_INTEGER);
        }),
        untilDestroyed(this),
      )
      .subscribe();
  }

  submit() {
    const lastTextValue = this.lastTextValue$.value.trim();
    const data = this.commentForm.getRawValue();

    const extractMentions = () => {
      const mentions = new Set<number>();
      const parsedComment = JSON.parse(data.comment);
      (parsedComment?.['ops'] || []).forEach((chunk: { insert?: { mention?: { id?: string } } }) => {
        const userId = chunk?.['insert']?.['mention']?.['id'];
        if (userId) {
          mentions.add(parseInt(userId, 10));
        }
      });

      return [...mentions];
    };

    const mentions = data.comment ? extractMentions() : [];
    const haveMentions = !!mentions.length;

    const commentJustTextWithoutMentions = lastTextValue;

    const validComment = !!commentJustTextWithoutMentions || haveMentions;
    if (validComment) {
      this.commented.emit({ content: data.comment, mentionUserIds: mentions, contentHtml: this.lastHtmlValue$.value });
      this.commentForm.reset();
    } else {
      console.log('invalid data in comment form', this.commentForm.getRawValue());
    }
  }

  cancel() {
    this.canceled.emit();
  }

  editorCreated(editorInstance: Quill) {
    this.editorInstance = editorInstance;
  }

  contentChanged(content: ContentChange) {
    this.lastTextValue$.next(content.text);
    this.lastHtmlValue$.next(content.html || '');
  }

  private mentionItemTemplate(data: UserMentionItem) {
    const component = this.viewContainerRef.createComponent(ChatAvatarComponent);
    component.instance.avatar = { full_name: data.value, imgSrc: data.profilePic || null, isUnknown: false };
    component.changeDetectorRef.detectChanges();
    const templateHtml = `<div class="flex items-center">${component.location.nativeElement.innerHTML} <span class="ml-2">${data.value}</span></div>`;
    component.destroy();
    return templateHtml;
  }
}
